import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../lib/api';
import {
  fetchFeedback,
  formatFeedbackDate,
  pct,
  RANGE_LABELS,
  starRow,
  type FeedbackComment,
  type FeedbackCoaching,
  type FeedbackDistRow,
  type FeedbackLeader,
  type FeedbackRange,
  type FeedbackReport,
} from '../lib/feedback';
import '../feedback.css';

export function CustomerFeedback() {
  const [range, setRange] = useState<FeedbackRange>('yesterday');
  const [report, setReport] = useState<FeedbackReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Drill-down: clicking a distribution row filters the comments to that star.
  const [starFilter, setStarFilter] = useState<number | null>(null);
  const latestRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Every response carries bounds.mx (the latest delivery day, computed
      // unfiltered), and the default (Yesterday) is calendar-based, so a single
      // windowed fetch is enough — last7/30/90 anchor off the cached bounds.mx
      // from a prior response.
      const rep = await fetchFeedback(range, latestRef.current);
      if (rep.bounds?.mx) latestRef.current = rep.bounds.mx;
      setReport(rep);
    } catch (err) {
      console.error('Feedback report load failed', err);
      setError(
        err instanceof ApiError
          ? `Could not load customer feedback: ${err.message} (${err.status})`
          : err instanceof Error
            ? `Could not load customer feedback: ${err.message}`
            : 'Failed to load customer feedback',
      );
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="fb">
      <Header range={range} loading={loading} onRange={setRange} onRefresh={() => void load()} />

      {error && (
        <p className="fb-error" role="alert">
          {error}
        </p>
      )}

      {report && (
        <>
          <SummaryTiles report={report} />
          <Distribution
            rows={report.distribution}
            total={report.totals.total_ratings}
            active={starFilter}
            onPick={(s) => setStarFilter((cur) => (cur === s ? null : s))}
          />
          <div className="fb-grid">
            <Coaching rows={report.coaching} />
            <Leaderboard rows={report.leaderboard} />
          </div>
          <Comments comments={report.comments} starFilter={starFilter} onClear={() => setStarFilter(null)} />
        </>
      )}

      {loading && !report && <p className="muted">Loading customer feedback…</p>}
    </div>
  );
}

// ─── Header ─────────────────────────────────────────────────────────────────

function Header({
  range,
  loading,
  onRange,
  onRefresh,
}: {
  range: FeedbackRange;
  loading: boolean;
  onRange: (r: FeedbackRange) => void;
  onRefresh: () => void;
}) {
  return (
    <header className="fb-head">
      <div className="fb-brand">
        <div className="fb-logo">BL</div>
        <div>
          <div className="fb-eyebrow">Bing Lee · Powered by Locate2u</div>
          <h1>Customer Feedback</h1>
        </div>
      </div>
      <div className="fb-head-right">
        <select
          className="fb-range"
          value={range}
          onChange={(e) => onRange(e.target.value as FeedbackRange)}
          aria-label="Window"
        >
          {(Object.keys(RANGE_LABELS) as FeedbackRange[]).map((r) => (
            <option key={r} value={r}>
              {RANGE_LABELS[r]}
            </option>
          ))}
        </select>
        <button type="button" className="fb-btn" onClick={onRefresh} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
    </header>
  );
}

// ─── Summary tiles ────────────────────────────────────────────────────────

function SummaryTiles({ report }: { report: FeedbackReport }) {
  const t = report.totals;
  const avg = t.avg_rating ?? 0;
  const hasDenom = t.completed_stops > 0;
  const responseRate = pct(t.total_ratings, t.completed_stops);
  const lowPct = pct(t.low_ratings, t.total_ratings);

  return (
    <div className="fb-tiles">
      <div className="fb-tile fb-tile-hero">
        <div className="fb-tile-label">Average rating</div>
        <div className="fb-hero-row">
          <span className="fb-hero-value num">{avg.toFixed(2)}</span>
          <span className="fb-hero-outof">/ 5</span>
        </div>
        <Stars rating={avg} className="fb-hero-stars" />
        <div className="fb-tile-sub">Across {t.total_ratings.toLocaleString()} customer ratings</div>
      </div>

      <div className="fb-tile">
        <div className="fb-tile-label">Response rate</div>
        <div className="fb-tile-value num">
          {t.total_ratings.toLocaleString()}
          <span className="fb-tile-denom"> / {t.completed_stops.toLocaleString()}</span>
        </div>
        <div className="fb-tile-note">
          {hasDenom
            ? `${responseRate.toFixed(2)}% of completed stops were rated`
            : 'No completed stops in this window yet'}
        </div>
        <div className="fb-progress">
          <div style={{ width: `${hasDenom ? Math.min(responseRate, 100) : 0}%` }} />
        </div>
        <div className="fb-tile-sub">Rated vs completed stops in the window</div>
      </div>

      <div className="fb-tile">
        <div className="fb-tile-label">Low ratings (≤ 2★)</div>
        <div className="fb-tile-value num fb-bad">
          {t.low_ratings.toLocaleString()}
          <span className="fb-tile-denom fb-bad-dim"> ({lowPct.toFixed(2)}%)</span>
        </div>
        <div className="fb-tile-sub fb-tile-sub-pad">Customers who rated 2 stars or below</div>
      </div>
    </div>
  );
}

// ─── Distribution ───────────────────────────────────────────────────────────

function Distribution({
  rows,
  total,
  active,
  onPick,
}: {
  rows: FeedbackDistRow[];
  total: number;
  active: number | null;
  onPick: (stars: number) => void;
}) {
  return (
    <section className="fb-card">
      <div className="fb-card-head">
        <div>
          <div className="fb-card-eyebrow">Distribution</div>
          <div className="fb-card-title">Ratings breakdown</div>
          <div className="fb-card-sub">Click a row to filter the comments below to that rating</div>
        </div>
      </div>
      <div className="fb-dist">
        {rows.map((r) => {
          const p = pct(r.count, total);
          const tone = r.stars >= 3 ? 'ok' : 'bad';
          return (
            <button
              type="button"
              key={r.stars}
              className={`fb-dist-row ${active === r.stars ? 'active' : ''}`}
              onClick={() => onPick(r.stars)}
              aria-pressed={active === r.stars}
            >
              <Stars rating={r.stars} fixed className="fb-dist-stars" />
              <div className="fb-bar">
                <div className={`fb-bar-fill ${tone}`} style={{ width: `${p}%` }} />
              </div>
              <span className="fb-dist-pct num">{p.toFixed(1)}%</span>
              <span className="fb-dist-count num">{r.count.toLocaleString()}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

// ─── Coaching: drivers needing attention ────────────────────────────────────

function Coaching({ rows }: { rows: FeedbackCoaching[] }) {
  const maxLow = rows.reduce((m, r) => Math.max(m, r.low_ratings), 0) || 1;
  return (
    <section className="fb-card">
      <div className="fb-card-head">
        <div>
          <div className="fb-card-eyebrow">Coaching</div>
          <div className="fb-card-title">Drivers needing attention</div>
          <div className="fb-card-sub">
            Drivers with the most low ratings (≤ 2★) out of their total ratings in this window —
            candidates for additional training
          </div>
        </div>
        <span className="fb-flag">⚠ Action needed</span>
      </div>

      <div className="fb-rowhead fb-coach-grid">
        <span>#</span>
        <span>Driver</span>
        <span />
        <span className="fb-r">Low ratings / total</span>
        <span className="fb-r">% low</span>
      </div>

      {rows.length === 0 && <div className="fb-empty">No low ratings in this window 🎉</div>}

      {rows.map((r, i) => (
        <div className="fb-coach-grid fb-coach-row" key={r.driver}>
          <span className="fb-rank">{i + 1}</span>
          <span className="fb-driver">{r.driver}</span>
          <div className="fb-bar">
            <div className="fb-bar-fill bad" style={{ width: `${pct(r.low_ratings, maxLow)}%` }} />
          </div>
          <span className="fb-r num">
            <strong className="fb-bad">{r.low_ratings}</strong>{' '}
            <span className="muted">/ {r.total_ratings}</span>
          </span>
          <span className="fb-r num muted">{r.low_pct.toFixed(1)}%</span>
        </div>
      ))}
    </section>
  );
}

// ─── Leaderboard ────────────────────────────────────────────────────────────

function Leaderboard({ rows }: { rows: FeedbackLeader[] }) {
  const [best, setBest] = useState(true);
  // The RPC returns best-first; reverse for worst-first.
  const ordered = best ? rows : [...rows].slice().reverse();

  return (
    <section className="fb-card">
      <div className="fb-card-head">
        <div>
          <div className="fb-card-eyebrow">People</div>
          <div className="fb-card-title">Driver leaderboard</div>
          <div className="fb-card-sub">
            Drivers with the {best ? 'highest' : 'lowest'} average rating in this window
          </div>
        </div>
        <button type="button" className="fb-btn fb-sort" onClick={() => setBest((b) => !b)}>
          {best ? '↓ Best first' : '↑ Worst first'}
        </button>
      </div>

      <div className="fb-lb-scroll">
        {ordered.map((r, i) => (
          <div className="fb-lb-row" key={r.driver}>
            <span className="fb-rank">{best ? i + 1 : rows.length - i}</span>
            <span className="fb-driver">{r.driver}</span>
            <Stars rating={r.avg_rating} className="fb-lb-stars" />
            <span className="fb-lb-avg num">{r.avg_rating.toFixed(2)}</span>
            <span className="fb-lb-count muted">{r.ratings} ratings</span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Recent comments (with star drill-down) ─────────────────────────────────

function Comments({
  comments,
  starFilter,
  onClear,
}: {
  comments: FeedbackComment[];
  starFilter: number | null;
  onClear: () => void;
}) {
  const shown = starFilter == null ? comments : comments.filter((c) => c.stars === starFilter);
  return (
    <section className="fb-card">
      <div className="fb-card-head">
        <div>
          <div className="fb-card-eyebrow">Verbatim</div>
          <div className="fb-card-title">Recent comments</div>
          <div className="fb-card-sub">
            {starFilter == null
              ? 'Latest customer comments in this window'
              : `Showing ${starFilter}★ comments`}
          </div>
        </div>
        {starFilter != null && (
          <button type="button" className="fb-btn" onClick={onClear}>
            Clear filter
          </button>
        )}
      </div>

      {shown.length === 0 ? (
        <div className="fb-empty">No comments to show</div>
      ) : (
        <div className="fb-comments">
          {shown.map((c, i) => (
            <div className={`fb-comment ${c.stars <= 2 ? 'low' : ''}`} key={`${c.stop_ref}-${i}`}>
              <div className="fb-comment-top">
                <Stars rating={c.stars} fixed className="fb-comment-stars" />
                <span className="fb-comment-driver">{c.driver}</span>
                <span className="fb-comment-meta">
                  {[c.city, c.province].filter(Boolean).join(', ') || '—'} · {formatFeedbackDate(c.created)}
                </span>
              </div>
              <div className="fb-comment-text">“{c.feedback}”</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ─── Stars ──────────────────────────────────────────────────────────────────

function Stars({
  rating,
  fixed = false,
  className = '',
}: {
  rating: number;
  // `fixed` renders exactly `rating` filled stars (for whole-number contexts);
  // otherwise rounds to nearest.
  fixed?: boolean;
  className?: string;
}) {
  const filled = fixed ? rating : 0;
  const states = fixed ? [1, 2, 3, 4, 5].map((i) => i <= filled) : starRow(rating);
  return (
    <span className={`fb-stars ${className}`} aria-label={`${rating} out of 5 stars`}>
      {states.map((on, i) => (
        <span key={i} className={on ? 'on' : 'off'}>
          ★
        </span>
      ))}
    </span>
  );
}
